/** Convert a department label into the Firebase-safe key used by workflow steps. */
export const sanitizeWorkflowDepartmentKey = (department) => String(department || '').replace(/[.#$/[\]]/g, '_');

/** Keep global steps and steps explicitly enabled for the selected department. */
export const filterWorkflowSubstagesByDepartment = (substages, department) => {
  return (substages || []).filter((substage) => {
    if (!substage?.departments || Object.keys(substage.departments).length === 0) return true;
    if (!department) return true;
    return substage.departments[sanitizeWorkflowDepartmentKey(department)] === true;
  });
};

const timestampFromSubstageId = (substage) => {
  const timestamp = parseInt(String(substage?.id || '').split('-').pop(), 10);
  return Number.isFinite(timestamp) ? timestamp : 0;
};

/**
 * Resolve the product's canonical substage order.
 *
 * Steps sharing an `afterId` are newest-first. If their anchor is hidden by a
 * department rule, they inherit the hidden anchor's parent position. Keeping
 * this helper shared by the board and popup prevents the two UIs from drifting.
 */
export const orderWorkflowSubstages = (substages, department) => {
  const allSubstages = [...(substages || [])];
  const visibleSubstages = filterWorkflowSubstagesByDepartment(allSubstages, department);
  const byId = Object.fromEntries(allSubstages.map((substage) => [substage.id, substage]));
  const visibleIds = new Set(visibleSubstages.map((substage) => substage.id));
  const ordered = visibleSubstages
    .filter((substage) => !substage.afterId)
    .sort((left, right) => timestampFromSubstageId(right) - timestampFromSubstageId(left));
  const groupedByAnchor = {};

  visibleSubstages.filter((substage) => substage.afterId).forEach((substage) => {
    if (!groupedByAnchor[substage.afterId]) groupedByAnchor[substage.afterId] = [];
    groupedByAnchor[substage.afterId].push(substage);
  });
  Object.values(groupedByAnchor).forEach((group) => {
    group.sort((left, right) => timestampFromSubstageId(right) - timestampFromSubstageId(left));
  });

  const placed = new Set(ordered.map((substage) => substage.id));
  let guard = 0;
  while (Object.keys(groupedByAnchor).length > 0 && guard < 100) {
    let madeProgress = false;

    Object.keys(groupedByAnchor).forEach((afterId) => {
      const group = groupedByAnchor[afterId];
      if (!group.length) {
        delete groupedByAnchor[afterId];
        return;
      }

      if (visibleIds.has(afterId) && placed.has(afterId)) {
        const anchorIndex = ordered.findIndex((substage) => substage.id === afterId);
        for (let index = group.length - 1; index >= 0; index -= 1) {
          ordered.splice(anchorIndex + 1, 0, group[index]);
          placed.add(group[index].id);
        }
        delete groupedByAnchor[afterId];
        madeProgress = true;
        return;
      }

      if (!visibleIds.has(afterId)) {
        const hiddenParentId = byId[afterId]?.afterId;
        if (hiddenParentId && placed.has(hiddenParentId)) {
          const anchorIndex = ordered.findIndex((substage) => substage.id === hiddenParentId);
          for (let index = group.length - 1; index >= 0; index -= 1) {
            ordered.splice(anchorIndex + 1, 0, group[index]);
            placed.add(group[index].id);
          }
          delete groupedByAnchor[afterId];
          madeProgress = true;
        } else if (!hiddenParentId) {
          ordered.push(...group);
          group.forEach((substage) => placed.add(substage.id));
          delete groupedByAnchor[afterId];
          madeProgress = true;
        }
      }
    });

    if (!madeProgress) {
      Object.values(groupedByAnchor).forEach((group) => ordered.push(...group));
      break;
    }
    guard += 1;
  }

  if (guard >= 100) {
    Object.values(groupedByAnchor).forEach((group) => ordered.push(...group));
  }
  return ordered;
};
